import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getLanguages } from '../../redux/translationSlice';
import { FEATURE_KEYS, isFeatureEnabled } from '../../redux/featureSlice';
import {
  Stethoscope, Heart, Clock, Shield, Activity, Users,
  Languages, Menu, X, Star, ArrowRight, Check, Play
} from 'lucide-react';
import logo from '../../assets/img/logo-TT2.png';
import healthcareIllustration from '../../assets/img/12810348_5063407.svg';
import LandingPackages from '../../components/subscription/LandingPackages';

export default function Home() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation('home');
  const { t: tHeader } = useTranslation('header');
  const languages = useSelector((state) => state.translation?.languages || []);
  const doctorRegEnabled = useSelector((s) => isFeatureEnabled(s, FEATURE_KEYS.authDoctorRegistration));
  const patientRegEnabled = useSelector((s) => isFeatureEnabled(s, FEATURE_KEYS.authPatientRegistration));
  // CTAs that route to /register-choice should disappear entirely if both
  // sign-up flows are disabled. If at least one is on, keep the CTA — the
  // RegisterChoice page renders only the enabled card.
  const anyRegistrationEnabled = doctorRegEnabled || patientRegEnabled;
  const [isLanguagePopupOpen, setIsLanguagePopupOpen] = useState(false);

  useEffect(() => {
    if (!languages || languages.length === 0) {
      dispatch(getLanguages({ page: 1, limit: 100, search: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const desktopLanguagePopupRef = useRef(null);
  const mobileLanguagePopupRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const handleLanguageChange = (lang) => {
    console.log('lang', lang);
    i18n.changeLanguage(lang);
    setIsLanguagePopupOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        (desktopLanguagePopupRef.current && !desktopLanguagePopupRef.current.contains(event.target)) &&
        (mobileLanguagePopupRef.current && !mobileLanguagePopupRef.current.contains(event.target))
      ) {
        setIsLanguagePopupOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSmoothScroll = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const handleSignInClick = () => {
    // Check if user is authenticated with all required data
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    const role = localStorage.getItem('Role');
    const id = localStorage.getItem('ID');
    const validRoles = ['admin', 'doctor', 'patient'];

    // If all authentication data is available, navigate to appropriate dashboard
    if (isAuthenticated && role && id && validRoles.includes(role)) {
      const roleRoutes = {
        doctor: '/doctorDashboard',
        admin: '/adminDashboard',
        patient: '/patientDashboard',
      };
      navigate(roleRoutes[role] || '/dashboard');
    } else {
      // If data is not available, navigate to login page
      navigate('/login');
    }
  };

  return (
    <div className="w-full min-h-screen bg-white" data-testid="home-container">
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-fadeInUp { animation: fadeInUp 0.6s ease-out forwards; }
        .animate-slideInLeft { animation: slideInLeft 0.6s ease-out forwards; }
        .animate-slideInRight { animation: slideInRight 0.6s ease-out forwards; }
        .nav-animation-1 { animation-delay: 0s; }
        .nav-animation-2 { animation-delay: 0.1s; }
        .nav-animation-3 { animation-delay: 0.2s; }
      `}</style>

      {/* NAVIGATION */}
      <nav className="fixed top-0 w-full bg-white shadow-lg z-50" data-testid="home-navigation">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2" data-testid="home-brand-container">
              <img src={logo} className="w-10 h-10" data-testid="home-brand-logo" />
              <span className="text-2xl font-bold text-[#41a4a4]" data-testid="home-brand-name">{t('home.brand_name', 'Therapeu Time')}</span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8" data-testid="home-desktop-menu">
              <a href="#features" onClick={(e) => handleSmoothScroll(e, 'features')} className="text-gray-700 hover:text-[#4ABDBD] transition cursor-pointer" data-testid="home-nav-features-link">{t('home.nav_features')}</a>
              <a href="#how-it-works" onClick={(e) => handleSmoothScroll(e, 'how-it-works')} className="text-gray-700 hover:text-[#4ABDBD] transition cursor-pointer" data-testid="home-nav-how-it-works-link">{t('home.nav_how_it_works')}</a>
              <a href="#about" onClick={(e) => handleSmoothScroll(e, 'about')} className="text-gray-700 hover:text-[#4ABDBD] transition cursor-pointer" data-testid="home-nav-about-link">{t('home.nav_about')}</a>
              <div ref={desktopLanguagePopupRef} className="relative" data-testid="home-desktop-language-container">
                <button
                  onClick={() => setIsLanguagePopupOpen(!isLanguagePopupOpen)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                  title={t('home.select_language')}
                  data-testid="home-desktop-language-button"
                >
                  <Languages className="w-5 h-5 text-gray-700" data-testid="home-desktop-language-icon" />
                </button>
                {isLanguagePopupOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 w-48 max-h-72 overflow-y-auto p-1" data-testid="home-desktop-language-dropdown">
                    {languages && languages.length > 0 ? (
                      languages.map((langItem) => {
                        const code = (langItem.code || '').toLowerCase();
                        const active = i18n.language?.startsWith(code);
                        return (
                          <button
                            key={langItem.languageId || code}
                            onClick={() => handleLanguageChange(code)}
                            className={`block w-full text-left px-4 py-2 text-sm rounded-md transition ${active ? 'bg-[#4ABDBD] text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                            data-testid={`home-desktop-language-option-${code}`}
                          >
                            <span className="flex items-center justify-between gap-2">
                              <span className="truncate">{langItem.name}</span>
                              <span className={`text-[10px] font-bold uppercase ${active ? 'text-white/80' : 'text-gray-400'}`}>{code}</span>
                            </span>
                          </button>
                        );
                      })
                    ) : (
                      <button
                        onClick={() => handleLanguageChange('en')}
                        className={`block w-full text-left px-4 py-2 text-sm rounded-md transition ${i18n.language?.startsWith('en') ? 'bg-[#4ABDBD] text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                        data-testid="home-desktop-language-option-en"
                      >
                        {t('home.language_english')}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3" data-testid="home-desktop-auth-buttons">
                <button
                  onClick={handleSignInClick}
                  className="px-6 py-2 text-[#4ABDBD] border border-[#4ABDBD] rounded-lg hover:bg-[#4ABDBD] hover:text-white transition cursor-pointer"
                  data-testid="home-desktop-sign-in-button"
                >
                  {t('home.nav_sign_in')}
                </button>
                {anyRegistrationEnabled && (
                  <button
                    onClick={() => navigate('/register-choice')}
                    className="px-6 py-2 bg-[#4ABDBD] text-white rounded-lg hover:bg-[#3A9B9B] transition cursor-pointer"
                    data-testid="home-desktop-get-started-button"
                  >
                    {t('home.nav_get_started')}
                  </button>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2" data-testid="home-mobile-controls">
              <div ref={mobileLanguagePopupRef} className="relative" data-testid="home-mobile-language-container">
                <button
                  onClick={() => setIsLanguagePopupOpen(!isLanguagePopupOpen)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  title={t('home.select_language')}
                  data-testid="home-mobile-language-button"
                >
                  <Languages className="w-5 h-5 text-gray-700" data-testid="home-mobile-language-icon" />
                </button>
                {isLanguagePopupOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 w-48 max-h-72 overflow-y-auto p-1" data-testid="home-mobile-language-dropdown">
                    {languages && languages.length > 0 ? (
                      languages.map((langItem) => {
                        const code = (langItem.code || '').toLowerCase();
                        const active = i18n.language?.startsWith(code);
                        return (
                          <button
                            key={langItem.languageId || code}
                            onClick={() => handleLanguageChange(code)}
                            className={`block w-full text-left px-4 py-2 text-sm rounded-md transition ${active ? 'bg-[#4ABDBD] text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                            data-testid={`home-mobile-language-option-${code}`}
                          >
                            <span className="flex items-center justify-between gap-2">
                              <span className="truncate">{langItem.name}</span>
                              <span className={`text-[10px] font-bold uppercase ${active ? 'text-white/80' : 'text-gray-400'}`}>{code}</span>
                            </span>
                          </button>
                        );
                      })
                    ) : (
                      <button
                        onClick={() => handleLanguageChange('en')}
                        className={`block w-full text-left px-4 py-2 text-sm rounded-md transition ${i18n.language?.startsWith('en') ? 'bg-[#4ABDBD] text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                        data-testid="home-mobile-language-option-en"
                      >
                        {t('home.language_english')}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 hover:bg-gray-100 rounded-lg"
                data-testid="home-mobile-menu-toggle-button"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6 text-gray-700" data-testid="home-mobile-menu-close-icon" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-700" data-testid="home-mobile-menu-open-icon" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div ref={mobileMenuRef} className="md:hidden border-t border-gray-200" data-testid="home-mobile-menu">
            <div className="px-4 py-3 space-y-3">
              <a href="#features" onClick={(e) => handleSmoothScroll(e, 'features')} className="block text-gray-700 hover:text-[#4ABDBD] cursor-pointer" data-testid="home-mobile-nav-features-link">{t('home.nav_features')}</a>
              <a href="#how-it-works" onClick={(e) => handleSmoothScroll(e, 'how-it-works')} className="block text-gray-700 hover:text-[#4ABDBD] cursor-pointer" data-testid="home-mobile-nav-how-it-works-link">{t('home.nav_how_it_works')}</a>
              <a href="#about" onClick={(e) => handleSmoothScroll(e, 'about')} className="block text-gray-700 hover:text-[#4ABDBD] cursor-pointer" data-testid="home-mobile-nav-about-link">{t('home.nav_about')}</a>
              <button
                onClick={handleSignInClick}
                className="w-full px-4 py-2 text-[#4ABDBD] border border-[#4ABDBD] rounded-lg hover:bg-[#4ABDBD] hover:text-white transition cursor-pointer"
                data-testid="home-mobile-sign-in-button"
              >
                {t('home.nav_sign_in')}
              </button>
              {anyRegistrationEnabled && (
                <button
                  onClick={() => navigate('/register-choice')}
                  className="w-full px-4 py-2 bg-[#4ABDBD] text-white rounded-lg hover:bg-[#3A9B9B] transition cursor-pointer"
                  data-testid="home-mobile-get-started-button"
                >
                  {t('home.nav_get_started')}
                </button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* HERO SECTION */}
      <section className="pt-44 pb-36 px-4 sm:px-6 lg:px-8 bg-linear-to-br from-[#f0faf9] via-white to-[#f0faf9]" data-testid="home-hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-slideInLeft" data-testid="home-hero-content">
              <h1 className="gradient-heading text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight" data-testid="home-hero-tagline">
                {t('home.tagline').split(',')[0]}, <span className="text-[#4ABDBD]">{t('home.tagline').split(',')[1]}</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-testid="home-hero-subtitle">
                {t('home.subtitle')}
              </p>
              {anyRegistrationEnabled && (
                <div className="flex flex-col gap-4">
                  <p className="text-xl font-semibold text-gray-900 mb-2" data-testid="home-register-as-text">
                    {t('home.register_as')}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4" data-testid="home-register-buttons">
                    {doctorRegEnabled && (
                      <button
                        onClick={() => navigate('/doctor-register')}
                        className="px-8 py-4 border-2 border-[#4ABDBD] text-[#4ABDBD] font-semibold rounded-lg hover:bg-[#f0faf9] transition flex items-center justify-center gap-2 cursor-pointer"
                        data-testid="home-doctor-register-button"
                      >
                        <Stethoscope className="w-5 h-5" data-testid="home-doctor-register-icon" />
                        {t('home.doctor')}
                      </button>
                    )}
                    {patientRegEnabled && (
                      <button
                        onClick={() => navigate('/patient-register')}
                        className="px-8 py-4 border-2 border-[#4ABDBD] text-[#4ABDBD] font-semibold rounded-lg hover:bg-[#f0faf9] transition flex items-center justify-center gap-2 cursor-pointer"
                        data-testid="home-patient-register-button"
                      >
                        <Users className="w-5 h-5" data-testid="home-patient-register-icon" />
                        {t('home.patient')}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="animate-slideInRight flex justify-center" data-testid="home-hero-illustration-container">
              <img
                src={healthcareIllustration}
                alt={t('home.healthcare_illustration')}
                className="w-full max-w-2xl h-auto"
                data-testid="home-hero-illustration"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES SECTION */}
      <section id="features" className="py-30 pb-36 px-4 sm:px-6 lg:px-8 bg-white" data-testid="home-features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16" data-testid="home-features-header">
            <h2 className="gradient-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="home-features-title">
              {t('home.features_title')}
            </h2>
            <p className="text-lg text-gray-600" data-testid="home-features-subtitle">{t('home.features_subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-testid="home-features-grid">
            {[
              { icon: Clock, title: t('home.feature1_title'), desc: t('home.feature1_desc') },
              { icon: Shield, title: t('home.feature2_title'), desc: t('home.feature2_desc') },
              { icon: Heart, title: t('home.feature3_title'), desc: t('home.feature3_desc') },
              { icon: Users, title: t('home.feature4_title'), desc: t('home.feature4_desc') },
              { icon: Activity, title: t('home.feature5_title'), desc: t('home.feature5_desc') },
              { icon: Stethoscope, title: t('home.feature6_title'), desc: t('home.feature6_desc') },
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="p-8 bg-white border-2 border-gray-100 rounded-2xl hover:border-[#4ABDBD] hover:shadow-lg transition group" data-testid={`home-feature-card-${index}`}>
                  <div className="w-16 h-16 bg-linear-to-br from-[#4ABDBD] to-[#3A9B9B] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#4ABDBD] transition" data-testid={`home-feature-icon-container-${index}`}>
                    <Icon className="w-8 h-8 text-white group-hover:text-white transition" data-testid={`home-feature-icon-${index}`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-testid={`home-feature-title-${index}`}>{feature.title}</h3>
                  <p className="text-gray-600" data-testid={`home-feature-desc-${index}`}>{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section id="how-it-works" className="py-30 pb-36 px-4 sm:px-6 lg:px-8 bg-[#f8fefD]" data-testid="home-how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16" data-testid="home-how-it-works-header">
            <h2 className="gradient-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="home-how-it-works-title">
              {t('home.how_it_works_title')}
            </h2>
          </div>
          <div className="space-y-8 max-w-3xl mx-auto" data-testid="home-how-it-works-steps">
            {[
              { step: 1, title: t('home.step1_title'), desc: t('home.step1_desc') },
              { step: 2, title: t('home.step2_title'), desc: t('home.step2_desc') },
              { step: 3, title: t('home.step3_title'), desc: t('home.step3_desc') },
              { step: 4, title: t('home.step4_title'), desc: t('home.step4_desc') },
            ].map((item, index) => (
              <div key={index} className="flex gap-6 items-start" data-testid={`home-step-${item.step}`}>
                <div className="shrink-0 w-12 h-12 bg-[#4ABDBD] text-white rounded-full flex items-center justify-center font-bold text-lg" data-testid={`home-step-number-${item.step}`}>
                  {item.step}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-testid={`home-step-title-${item.step}`}>{item.title}</h3>
                  <p className="text-gray-600" data-testid={`home-step-desc-${item.step}`}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLANS & PRICING SECTION (public — subscribe after login / contact for custom) */}
      <LandingPackages />

      {/* ABOUT SECTION */}
      <section id="about" className="py-30 pb-36 px-4 sm:px-6 lg:px-8 bg-white" data-testid="home-about-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div data-testid="home-about-content">
              <h2 className="gradient-heading text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-testid="home-about-title">
                {t('home.about_title')}
              </h2>
              <p className="text-lg text-gray-600 mb-4 leading-relaxed" data-testid="home-about-desc1">
                {t('home.about_desc1')}
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-testid="home-about-desc2">
                {t('home.about_desc2')}
              </p>
              <div className="space-y-4" data-testid="home-about-features-list">
                {[
                  t('home.about_feature1'),
                  t('home.about_feature2'),
                  t('home.about_feature3'),
                  t('home.about_feature4'),
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3" data-testid={`home-about-feature-${index}`}>
                    <Check className="w-6 h-6 text-[#4ABDBD]" data-testid={`home-about-feature-icon-${index}`} />
                    <span className="text-gray-700 font-medium" data-testid={`home-about-feature-text-${index}`}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center" data-testid="home-about-illustration-container">
              <div className="w-80 h-80 bg-linear-to-br from-[#e8f8f6] to-[#d4f1ed] rounded-3xl flex items-center justify-center">
                <Users className="w-40 h-40 text-[#4ABDBD] opacity-60" data-testid="home-about-illustration-icon" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f8fefD]" data-testid="home-testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16" data-testid="home-testimonials-header">
            <h2 className="gradient-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="home-testimonials-title">
              {t('home.testimonials_title')}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-testid="home-testimonials-grid">
            {[
              { name: t('home.testimonial1_name'), role: t('home.testimonial1_role'), text: t('home.testimonial1_text') },
              { name: t('home.testimonial2_name'), role: t('home.testimonial2_role'), text: t('home.testimonial2_text') },
              { name: t('home.testimonial3_name'), role: t('home.testimonial3_role'), text: t('home.testimonial3_text') },
            ].map((testimonial, index) => (
              <div key={index} className="p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition" data-testid={`home-testimonial-card-${index}`}>
                <div className="flex gap-1 mb-4" data-testid={`home-testimonial-stars-${index}`}>
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" data-testid={`home-testimonial-star-${index}-${i}`} />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic" data-testid={`home-testimonial-text-${index}`}>"{testimonial.text}"</p>
                <p className="font-bold text-gray-900" data-testid={`home-testimonial-name-${index}`}>{testimonial.name}</p>
                <p className="text-sm text-gray-500" data-testid={`home-testimonial-role-${index}`}>{testimonial.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-linear-to-r from-[#4ABDBD] to-[#3A9B9B]" data-testid="home-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className=" text-4xl md:text-5xl font-bold text-white mb-6" data-testid="home-cta-title">
            {t('home.cta_main_title')}
          </h2>
          <p className="text-xl text-white opacity-90 mb-8" data-testid="home-cta-subtitle">
            {t('home.cta_subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-testid="home-cta-buttons">
            {anyRegistrationEnabled && (
              <button
                onClick={() => navigate('/register-choice')}
                className="px-8 py-4 bg-white text-[#4ABDBD] font-bold rounded-lg hover:bg-gray-100 transition flex items-center justify-center gap-2 cursor-pointer"
                data-testid="home-cta-register-button"
              >
                {t('home.cta_button1')} <ArrowRight className="w-5 h-5" data-testid="home-cta-register-icon" />
              </button>
            )}
            <button
              onClick={handleSignInClick}
              className="px-8 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-[#4ABDBD] transition cursor-pointer"
              data-testid="home-cta-sign-in-button"
            >
              {t('home.cta_button2')}
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8" data-testid="home-footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div data-testid="home-footer-brand-section">
              <div className="flex items-center gap-2 mb-4">
                <Stethoscope className="w-6 h-6 text-[#4ABDBD]" data-testid="home-footer-brand-icon" />
                <span className="text-xl font-bold text-[#4ABDBD]" data-testid="home-footer-brand-name">{t('home.brand_name', 'Therapeu Time')}</span>
              </div>
              <p className="text-gray-400" data-testid="home-footer-about-text">{t('home.footer_about')}</p>
            </div>
            <div data-testid="home-footer-product-section">
              <h4 className="font-bold mb-4" data-testid="home-footer-product-title">{t('home.footer_product')}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#features" className="hover:text-[#4ABDBD] transition" data-testid="home-footer-features-link">{t('home.nav_features')}</a></li>
                <li><a href="#how-it-works" className="hover:text-[#4ABDBD] transition" data-testid="home-footer-how-it-works-link">{t('home.nav_how_it_works')}</a></li>
                <li><a href="#about" className="hover:text-[#4ABDBD] transition" data-testid="home-footer-about-link">{t('home.nav_about')}</a></li>
              </ul>
            </div>
            <div data-testid="home-footer-company-section">
              <h4 className="font-bold mb-4" data-testid="home-footer-company-title">{t('home.footer_company')}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#" className="hover:text-[#4ABDBD] transition" data-testid="home-footer-privacy-link">{t('home.footer_privacy')}</a></li>
                <li><a href="#" className="hover:text-[#4ABDBD] transition" data-testid="home-footer-terms-link">{t('home.footer_terms')}</a></li>
                <li><a href="#" className="hover:text-[#4ABDBD] transition" data-testid="home-footer-contact-link">{t('home.footer_contact')}</a></li>
              </ul>
            </div>
            <div data-testid="home-footer-follow-section">
              <h4 className="font-bold mb-4" data-testid="home-footer-follow-title">{t('home.footer_follow')}</h4>
              <div className="flex gap-4 text-gray-400" data-testid="home-footer-social-links">
                <a href="#" className="hover:text-[#4ABDBD] transition" data-testid="home-footer-facebook-link">{t('home.facebook', 'Facebook')}</a>
                <a href="#" className="hover:text-[#4ABDBD] transition" data-testid="home-footer-twitter-link">{t('home.twitter', 'Twitter')}</a>
                <a href="#" className="hover:text-[#4ABDBD] transition" data-testid="home-footer-linkedin-link">{t('home.linkedin', 'LinkedIn')}</a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center text-gray-400 text-sm" data-testid="home-footer-copyright-container">
            <p data-testid="home-footer-copyright-text">{t('home.footer_copyright', { year: new Date().getFullYear() })}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
